import { Component } from '@theme/component';
import {
  onDocumentLoaded,
  changeMetaThemeColor,
  setHeaderMenuStyle,
  calculateHeaderGroupHeight,
  setTrackedHeight,
} from '@theme/utilities';
import { getScrollTop, getScrollEventTarget, getIntersectionRoot } from '@theme/scroll-container';

/**
 * @typedef {Object} HeaderComponentRefs
 * @property {HTMLDivElement} headerDrawerContainer - The header drawer container element
 * @property {HTMLElement} headerMenu - The header menu element
 */

/**
 * @typedef {CustomEvent<{ minimumReached: boolean }>} OverflowMinimumEvent
 */

/**
 * A custom element that manages the site header.
 *
 * @extends {Component<HeaderComponentRefs>}
 */

class HeaderComponent extends Component {
  requiredRefs = ['headerDrawerContainer', 'headerMenu'];

  /**
   * Width of window when header drawer was hidden
   * @type {number | null}
   */
  #menuDrawerHiddenWidth = null;

  /**
   * An intersection observer for monitoring sticky header position
   * @type {IntersectionObserver | null}
   */
  #intersectionObserver = null;

  /** @type {EventTarget | null} */
  #scrollContainer = null;

  /**
   * Whether the header has been scrolled offscreen, when sticky behavior is 'scroll-up'
   * @type {boolean}
   */
  #offscreen = false;

  /**
   * The last recorded scrollTop of the document, when sticky behavior is 'scroll-up
   * @type {number}
   */
  #lastScrollTop = 0;

  /**
   * A timeout to allow for hiding animation, when sticky behavior is 'scroll-up'
   * @type {number | null}
   */
  #timeout = null;

  /**
   * RAF ID for scroll handler throttling
   * @type {number | null}
   */
  #scrollRafId = null;

  /**
   * Keeps the global `--header-height` custom property up to date,
   * which other theme components can then consume
   */
  #resizeObserver = new ResizeObserver(([entry]) => {
    if (!entry || !entry.borderBoxSize[0]) return;

    // The initial height is calculated using the .offsetHeight property, which returns an integer.
    // We round to the nearest integer to avoid unnecessary reflows.
    const roundedHeaderHeight = Math.round(entry.borderBoxSize[0].blockSize);
    this.#stickyThreshold = null;

    // Check if the menu drawer should be hidden in favor of the header menu
    if (this.#menuDrawerHiddenWidth && window.innerWidth > this.#menuDrawerHiddenWidth) {
      this.#updateMenuVisibility(false);
    }

    // Read scroll state before the write below dirties page-wide style, or the read forces a layout.
    this.#updateStickyState();
    document.body.style.setProperty('--header-height', `${roundedHeaderHeight}px`);
  });

  /**
   * Keeps every element carrying `data-height-var` in step with the custom property it names. A
   * one-shot measurement goes stale when a late logo or webfont resizes something.
   * @type {ResizeObserver}
   */
  #rowObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      setTrackedHeight(entry.target, entry.borderBoxSize[0]?.blockSize ?? 0);
    }
  });

  /**
   * Sets the sticky state, mirroring it onto <html> so anything on the page can style against it.
   * @param {'inactive' | 'active' | 'idle'} state
   */
  #setStickyState = (state) => {
    this.dataset.stickyState = state;
    document.documentElement.dataset.stickyState = state;
    // Every sticky transition used to repeat this guard at the call site. `themeColor` is a static
    // attribute, so re-applying the same colour on the extra transitions is a no-op.
    if (this.dataset.themeColor) changeMetaThemeColor(this.dataset.themeColor);
  };

  /**
   * Observes the header while scrolling the viewport to track when its actively sticky
   * @param {Boolean} alwaysSticky - Determines if we need to observe when the header is offscreen
   */
  #observeStickyPosition = (alwaysSticky = true) => {
    if (this.#intersectionObserver) return;

    // 'always' mode measures against the sentinel on scroll instead; an observer only fires on
    // crossings, so it can't ignore the rebound described in #updateStickyState.
    if (alwaysSticky) {
      this.#stickySentinel = document.querySelector('[data-header-sticky-sentinel]');

      if (this.#stickySentinel) {
        this.#updateStickyState();
        return;
      }
    }

    this.#intersectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry) return;

        const { isIntersecting } = entry;

        if (alwaysSticky) {
          this.#setStickyState(isIntersecting ? 'inactive' : 'active');
        } else {
          this.#offscreen = !isIntersecting || this.dataset.stickyState === 'active';
        }
      },
      { threshold: alwaysSticky ? 1 : 0, root: getIntersectionRoot() },
    );

    this.#intersectionObserver.observe(this);
  };

  /**
   * Marks the scroll depth at which the header collapses.
   * @type {HTMLElement | null}
   */
  #stickySentinel = null;

  /** @type {number | null} */
  #stickyThreshold = null;

  /**
   * How much the header shrinks on collapse. Measured, since either state can be styled.
   * @type {number}
   */
  #collapseShift = 0;

  /** @type {HTMLElement | null} */
  #headerGroup = null;

  /** @type {Set<HTMLElement>} */
  #observedHeaderGroupElements = new Set();

  /** @type {ResizeObserver} */
  #headerGroupResizeObserver = new ResizeObserver(() => {
    this.#updateHeaderGroupHeight();
  });

  /** @type {MutationObserver} */
  #headerGroupMutationObserver = new MutationObserver(() => {
    this.#syncHeaderGroupObservers();
    this.#updateHeaderGroupHeight();
  });

  #updateHeaderGroupHeight() {
    this.#stickyThreshold = null;
    const headerGroupHeight = Math.round(calculateHeaderGroupHeight());
    document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
  }

  #syncHeaderGroupObservers() {
    if (!this.#headerGroup) return;

    const elementsToObserve = new Set();
    elementsToObserve.add(this);

    for (const element of this.#headerGroup.children) {
      if (element instanceof HTMLElement) elementsToObserve.add(element);
    }

    for (const element of this.#observedHeaderGroupElements) {
      if (!elementsToObserve.has(element)) this.#headerGroupResizeObserver.unobserve(element);
    }

    for (const element of elementsToObserve) {
      if (!this.#observedHeaderGroupElements.has(element)) {
        this.#headerGroupResizeObserver.observe(element);
      }
    }

    this.#observedHeaderGroupElements = elementsToObserve;
  }

  #connectHeaderGroupObservers() {
    const headerGroup = document.querySelector('#header-group');
    if (!(headerGroup instanceof HTMLElement)) return;

    this.#headerGroup = headerGroup;
    onDocumentLoaded(() => {
      if (!this.isConnected || this.#headerGroup !== headerGroup) return;

      this.#syncHeaderGroupObservers();
      this.#headerGroupMutationObserver.observe(headerGroup, { childList: true });
      this.#updateHeaderGroupHeight();
    });
  }

  /**
   * Collapses or releases the header based on scroll depth past the sentinel.
   *
   * Releasing uses a dead band of #collapseShift: collapsing shortens the header, and scroll
   * anchoring compensates by moving the scroll position far enough to read as "scrolled back up".
   */
  #updateStickyState = () => {
    const sentinel = this.#stickySentinel;
    if (!sentinel) return;

    if (!this.dataset.stickyState) this.#setStickyState('inactive');

    const isActive = this.dataset.stickyState === 'active';
    const scrollTop = getScrollTop();
    // At the top nothing can collapse; skip offsetTop so page load doesn't force a layout.
    if (!isActive && scrollTop <= 0) return;

    const threshold = this.#stickyThreshold ?? (this.#stickyThreshold = sentinel.offsetTop);

    if (!isActive && scrollTop > threshold) {
      const heightBefore = this.offsetHeight;

      this.#setStickyState('active');

      this.#collapseShift = Math.max(heightBefore - this.offsetHeight, 0);
    } else if (isActive && scrollTop < threshold - this.#collapseShift) {
      this.#setStickyState('inactive');
    }
  };

  /**
   * Handles the overflow minimum event from the header menu
   * @param {Event} event
   */
  #handleOverflowMinimum = (event) => {
    const { detail } = /** @type {OverflowMinimumEvent} */ (event);
    this.#updateMenuVisibility(detail.minimumReached);
  };

  /**
   * Updates the visibility of the menu and drawer
   * @param {boolean} hideMenu - Whether to hide the menu and show the drawer
   */
  #updateMenuVisibility(hideMenu) {
    if (hideMenu) {
      this.#menuDrawerHiddenWidth = window.innerWidth;
    } else {
      this.#menuDrawerHiddenWidth = null;

      const overflowList = this.querySelector('overflow-list');
      if (overflowList) overflowList.removeAttribute('minimum-reached');
    }
    setHeaderMenuStyle();
  }

  #handleWindowScroll = () => {
    if (this.#scrollRafId !== null) return;

    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState = () => {
    const stickyMode = this.getAttribute('sticky');
    if (!this.#offscreen && stickyMode !== 'always') return;

    const scrollTop = getScrollTop();
    const isScrollingUp = scrollTop < this.#lastScrollTop;
    const isAtTop =
      stickyMode === 'always' ? scrollTop <= 0 : this.getBoundingClientRect().top >= 0;

    if (this.#timeout) {
      clearTimeout(this.#timeout);
      this.#timeout = null;
    }

    if (stickyMode === 'always') {
      this.#updateStickyState();

      if (isAtTop) {
        this.dataset.scrollDirection = 'none';
      } else if (isScrollingUp) {
        this.dataset.scrollDirection = 'up';
      } else {
        this.dataset.scrollDirection = 'down';
      }

      this.#lastScrollTop = scrollTop;
      return;
    }

    if (isScrollingUp) {
      if (isAtTop) {
        // reset sticky state when header is scrolled up to natural position
        this.#offscreen = false;
        this.#setStickyState('inactive');
        this.dataset.scrollDirection = 'none';
      } else {
        // show sticky header when scrolling up
        this.#setStickyState('active');
        this.dataset.scrollDirection = 'up';
      }
    } else {
      this.dataset.scrollDirection = 'none';
      this.#setStickyState('idle');
    }

    this.#lastScrollTop = scrollTop;
  };

  connectedCallback() {
    super.connectedCallback();
    this.#resizeObserver.observe(this);
    this.#connectHeaderGroupObservers();

    for (const element of this.querySelectorAll('[data-height-var]')) {
      this.#rowObserver.observe(element);
    }

    this.addEventListener('overflowMinimum', this.#handleOverflowMinimum);

    if (this.querySelector('overflow-list[minimum-reached]')) {
      this.#updateMenuVisibility(true);
    }

    const stickyMode = this.getAttribute('sticky');
    if (stickyMode) {
      this.#observeStickyPosition(stickyMode === 'always');

      if (stickyMode === 'scroll-up' || stickyMode === 'always') {
        this.#scrollContainer = getScrollEventTarget();
        this.#scrollContainer.addEventListener('scroll', this.#handleWindowScroll);
      }
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.#rowObserver.disconnect();
    this.#intersectionObserver?.disconnect();
    this.#headerGroupResizeObserver.disconnect();
    this.#headerGroupMutationObserver.disconnect();
    this.#observedHeaderGroupElements.clear();
    this.#headerGroup = null;
    this.removeEventListener('overflowMinimum', this.#handleOverflowMinimum);
    this.#scrollContainer?.removeEventListener('scroll', this.#handleWindowScroll);
    this.#scrollContainer = null;
    if (this.#scrollRafId !== null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }
    document.body.style.setProperty('--header-height', '0px');
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}
